import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { snippets, teams } from '../api';
import type {
  SnippetShareResponse,
  TeamDetailResponse,
  TeamResponse,
  TeamRole,
} from '../api';

export const teamsKey = ['teams'] as const;
export const teamKey = (id: string) => ['team', id] as const;
export const sharesKey = (snippetId: string) => ['shares', snippetId] as const;

export function useTeams() {
  return useQuery({
    queryKey: teamsKey,
    queryFn: async (): Promise<TeamResponse[]> => {
      const { data, error } = await teams.list();
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });
}

export function useTeam(id: string | null) {
  return useQuery({
    queryKey: teamKey(id ?? ''),
    enabled: id !== null,
    queryFn: async (): Promise<TeamDetailResponse> => {
      const { data, error } = await teams.get({ path: { id: id! } });
      if (error) {
        throw error;
      }
      if (!data) {
        throw new Error('That team could not be loaded.');
      }
      return data;
    },
  });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await teams.create({ body: { name } });
      if (error) {
        throw error;
      }
      return data!;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: teamsKey }),
  });
}

// Adding a member directly is gone. Membership comes from somebody accepting an invitation —
// see useInviteMember in invitationQueries.ts.

export function useChangeRole(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { userId: string; role: TeamRole }) => {
      const { data, error } = await teams.changeRole({
        path: { id: teamId, userId: input.userId },
        body: { role: input.role },
      });
      if (error) {
        throw error;
      }
      return data!;
    },
    onSuccess: (team) => queryClient.setQueryData(teamKey(teamId), team),
  });
}

export function useRemoveMember(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await teams.removeMember({ path: { id: teamId, userId } });
      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamKey(teamId) });
      void queryClient.invalidateQueries({ queryKey: teamsKey });
      // Leaving a team takes its snippets and tags with it.
      void queryClient.invalidateQueries({ queryKey: ['snippets'] });
      void queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: string) => {
      const { error } = await teams.remove({ path: { id: teamId } });
      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: teamsKey });
      void queryClient.invalidateQueries({ queryKey: ['snippets'] });
      void queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export function useShares(snippetId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: sharesKey(snippetId ?? ''),
    // Listing recipients needs Manage, so asking without it would be a guaranteed 404.
    enabled: snippetId !== null && enabled,
    queryFn: async (): Promise<SnippetShareResponse[]> => {
      const { data, error } = await snippets.listShares({ path: { id: snippetId! } });
      if (error) {
        throw error;
      }
      return data ?? [];
    },
  });
}

// Sharing directly is gone too. A snippet is offered and accepted — see
// useShareSnippetByInvitation in invitationQueries.ts.

export function useUnshare(snippetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await snippets.unshare({ path: { id: snippetId, userId } });
      if (error) {
        throw error;
      }
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sharesKey(snippetId) }),
  });
}
